// src/hooks/useDialogFocus.js
//
// Keyboard behaviour for a modal dialog (.dialog-backdrop > .dialog): Tab and
// Shift+Tab cycle inside the dialog instead of escaping to the page behind it,
// and focus goes back to whatever opened it when it closes. Escape handling
// stays with each dialog (most already close on Esc and block it while saving).
//
//   const ref = useRef(null);
//   useDialogFocus(ref);
//   <div className="dialog" role="dialog" aria-modal="true" ref={ref}>…</div>
//
// Call it BEFORE any effect that moves focus into the dialog, so the opener is
// captured first.

import { useEffect } from 'react';

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

export default function useDialogFocus(ref) {
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const opener = document.activeElement;

    const onKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      const items = Array.from(node.querySelectorAll(FOCUSABLE))
        .filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (items.length === 0) { e.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!node.contains(active)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    };
  }, [ref]);
}
